import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { CopyToClipboard } from "react-copy-to-clipboard";
import ConfirmDeleteModal from './images/ConfirmDeleteModal';
import { usePermissions } from '../contexts/permissions';
import useNotification from '../hooks/notification';
import useMutation from '../hooks/useMutation';
import { REMOVE_ACCESS_LINK_MUTATION } from '../constants/graphql';
import { ACCESS_LINK_NAME } from '../contexts/AccessLinkContext';

const AccessLinkMoreOptionsMenu = ({ entity, disabled, setEntities }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  const { can } = usePermissions();
  const { notifySuccess, notifyError } = useNotification();
  const [removeAction] = can('accessLinks.destroy') ? useMutation(REMOVE_ACCESS_LINK_MUTATION) : [null];

  const open = Boolean(anchorEl);
  const canDelete = can('accessLinks.destroy') && removeAction;
  const accessLink = `${window.location.host}/shopping?${ACCESS_LINK_NAME}=${entity.token}`;

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleCopyLink = () => {
    handleClose();
    notifySuccess("Link copied to clipboard!");
  };

  const handleDeleteClick = () => {
    handleClose();
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    setRemoveLoading(true);
    setShowDeleteConfirm(false);

    removeAction({
      variables: { id: entity.id },
      onSuccess: () => {
        setRemoveLoading(false);
        notifySuccess("Access link has been successfully deleted!");
        if (setEntities) {
          setEntities(prev => prev.filter(item => item.id !== entity.id));
        }
      },
      onFailure: errors => {
        setRemoveLoading(false);
        notifyError(errors);
      },
    });
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        disabled={disabled || removeLoading}
        size="small"
        sx={{
          color: 'text.secondary',
          '&:hover': {
            color: 'text.primary',
          }
        }}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 180,
          }
        }}
        MenuListProps={{
          onClick: (e) => e.stopPropagation()
        }}
      >
        <CopyToClipboard text={accessLink} onCopy={handleCopyLink}>
          <MenuItem disabled={!entity.token}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy Link</ListItemText>
          </MenuItem>
        </CopyToClipboard>

        {canDelete && (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Link</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {showDeleteConfirm && (
        <ConfirmDeleteModal
          title="Delete Access Link"
          descr="Are you sure you want to delete this access link? This action cannot be undone."
          onClose={() => setShowDeleteConfirm(false)}
          onDelete={handleDeleteConfirm}
        />
      )}
    </>
  );
};

export default AccessLinkMoreOptionsMenu;
